import type { SafetyFieldConfig, SafetySectionDefinition } from './types';
import { unitFieldTooltips } from '@/features/companies/components/masterDataFieldTooltips';
import { generateSafetyFormSections } from '../../utils/safetyDisplayHelpers';
import { CATALOG_IDS } from '@/data/models/catalog';
import { THRESHOLD_TYPE_OPTIONS, THRESHOLD_UNIT_OPTIONS } from '../../refrigerationUnitFormOptions';

/**
 * Section definitions for Rilevazione (Detection) sub-tab.
 */
export const rilevazioneSections: Record<string, SafetySectionDefinition> = {
  detectors: { title: 'Sistemi di rilevazione', order: 1 },
  maintenance: { title: 'Manutenzione rilevazione', order: 2 },
  thresholds: { title: 'Soglie di intervento', order: 3 },
} as const;

/**
 * Field definitions for Rilevazione (Detection) sub-tab.
 *
 * Includes thresholds array field with schema-driven table editing.
 */
export const rilevazioneFields: readonly SafetyFieldConfig[] = [
  // Detectors section
  {
    key: 'detectorsToxicCount',
    dataPath: 'technical.safety.detectors.toxicCount',
    label: 'Rilevatori tossici',
    type: 'number',
    placeholder: 'Es: 3',
    tooltip: unitFieldTooltips.detection.detectorsToxicCount,
    panel: 'detectors',
    section: 'detectors',
  },
  {
    key: 'detectorsExplosiveCount',
    dataPath: 'technical.safety.detectors.explosiveCount',
    label: 'Rilevatori esplosivi',
    type: 'number',
    placeholder: 'Es: 1',
    tooltip: unitFieldTooltips.detection.detectorsExplosiveCount,
    panel: 'detectors',
    section: 'detectors',
  },
  {
    key: 'detectorsCeilingCount',
    dataPath: 'technical.safety.detectors.ceilingCount',
    label: 'Rilevatori quota soffitto',
    type: 'number',
    placeholder: 'Es: 2',
    tooltip: unitFieldTooltips.detection.detectorsCeilingCount,
    panel: 'detectors',
    section: 'detectors',
  },
  {
    key: 'detectorsHumanCount',
    dataPath: 'technical.safety.detectors.humanHeightCount',
    label: 'Rilevatori quota uomo',
    type: 'number',
    placeholder: 'Es: 1',
    tooltip: unitFieldTooltips.detection.detectorsHumanCount,
    panel: 'detectors',
    section: 'detectors',
  },
  {
    key: 'detectorPositions',
    dataPath: 'technical.safety.detectors.positions',
    label: 'Posizioni rilevatori',
    type: 'text', // Array - joined with ", " in view, resolved via catalogId
    placeholder: 'Es: Sala macchine, Locale evaporatori',
    tooltip: unitFieldTooltips.detection.detectorPositions,
    panel: 'detectors',
    section: 'detectors',
    colSpan: 2,
    catalogId: CATALOG_IDS.PLANT_LOCATIONS, // Resolve IDs to labels in display
    // Form uses customRenderers in SafetySubTabForm for CatalogSuggestTagsInput
  },
  {
    key: 'calibrationNotes',
    dataPath: 'technical.safety.detectors.calibrationNotes',
    label: 'Note calibrazione',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.detection.calibrationNotes,
    panel: 'detectors',
    section: 'detectors',
    colSpan: 2,
  },
  // Maintenance section
  {
    key: 'detectionMaintenanceName',
    dataPath: 'technical.safety.detectionMaintenance.name',
    label: 'Manutentore sistema rilevazione',
    type: 'text',
    placeholder: 'Es: Servizi Rilevazione S.r.l.',
    tooltip: unitFieldTooltips.detection.detectionMaintenanceName,
    panel: 'maintenance',
    section: 'maintenance',
  },
  {
    key: 'detectionMaintenanceNotes',
    dataPath: 'technical.safety.detectionMaintenance.notes',
    label: 'Note manutenzione rilevazione',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.detection.detectionMaintenanceNotes,
    panel: 'maintenance',
    section: 'maintenance',
    colSpan: 2,
  },
  // Thresholds array - rendered as embedded table with row modals
  {
    key: 'thresholds',
    dataPath: 'technical.safety.detectors.thresholds',
    label: 'Soglie di intervento',
    type: 'text', // Marker for array field (actual type determined by arrayConfig)
    panel: 'thresholds',
    section: 'thresholds',
    displayFormatter: 'skip', // Skip in scalar display (rendered as EmbeddedArrayTable)
    arrayConfig: {
      tableTitle: 'Soglie di intervento',
      modalTitle: 'Soglia di intervento',
      addLabel: 'Aggiungi soglia',
      emptyMessage: 'Nessuna soglia configurata. Aggiungi la prima soglia di intervento.',
      defaultValues: {
        id: '', // Will be generated on save
        thresholdType: 'tossicita',
        unit: 'ppm',
        value: 300,
        actions: [],
      },
      itemFields: [
        {
          key: 'thresholdType',
          label: 'Tipo',
          type: 'select',
          options: THRESHOLD_TYPE_OPTIONS,
          width: 'sm',
          tooltip: 'Tipo di rilevazione (tossicità o esplosività)',
        },
        {
          key: 'unit',
          label: 'Unità',
          type: 'select',
          options: THRESHOLD_UNIT_OPTIONS,
          width: 'sm',
          tooltip: 'Unità di misura della soglia',
        },
        {
          key: 'value',
          label: 'Valore',
          type: 'number',
          width: 'sm',
          placeholder: 'Es: 300',
          tooltip: 'Valore numerico della soglia',
        },
        {
          key: 'actions',
          label: 'Azioni',
          type: 'multi-select',
          catalogId: CATALOG_IDS.THRESHOLD_ACTIONS,
          width: 'flex',
          tooltip: 'Azioni da attivare al superamento della soglia',
        },
      ],
    },
  },
] as const;

/**
 * Scalar fields for display and form generation.
 * Excludes array fields (thresholds) which are handled separately.
 */
export const rilevazioneScalarFields = rilevazioneFields.filter((f) => !f.arrayConfig);

/**
 * Form sections derived from field definitions.
 * Includes all scalar fields - use customRenderers in SafetySubTabForm
 * for fields that need special components (CatalogSuggestTagsInput, etc.)
 */
export const rilevazioneFormSections = generateSafetyFormSections(
  rilevazioneScalarFields,
  ['detectors', 'maintenance'],
  {
    detectors: 'Sistemi di rilevazione',
    maintenance: 'Manutenzione rilevazione',
  }
);
